import { useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { maximumLogoBytes, type SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { logoAddress, logoFileAccept, prepareLogoUpload, type LogoFileProblem } from './logo';

export interface LogoPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
  /** Milliseconds timestamp of the current logo, or null when the club has none. */
  logoVersion: number | null;
  /** Reloads the signed-in account so the header shows the new logo straight away. */
  refreshAccount: () => Promise<void>;
}

type ErrorKey = ApiRequestError['messageKey'];

const problemKeys: Record<LogoFileProblem, 'logo.error.unsupportedType' | 'logo.error.tooLarge'> = {
  unsupportedType: 'logo.error.unsupportedType',
  tooLarge: 'logo.error.tooLarge',
};

/** Club administrators choose the logo every member sees in the page header. */
export function LogoPanel({
  locale,
  organizationId,
  csrfToken,
  logoVersion,
  refreshAccount,
}: LogoPanelProps): React.JSX.Element {
  const [errorKey, setErrorKey] = useState<ErrorKey | (typeof problemKeys)[LogoFileProblem] | null>(
    null,
  );
  const [saved, setSaved] = useState<'uploaded' | 'removed' | null>(null);
  const [busy, setBusy] = useState(false);
  const path = `/api/v1/organizations/${organizationId}/logo`;

  async function run(action: () => Promise<void>, outcome: 'uploaded' | 'removed'): Promise<void> {
    setErrorKey(null);
    setSaved(null);
    setBusy(true);
    try {
      await action();
      await refreshAccount();
      setSaved(outcome);
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    } finally {
      setBusy(false);
    }
  }

  async function choose(file: File | undefined): Promise<void> {
    if (file === undefined) return;
    setErrorKey(null);
    setSaved(null);
    const prepared = prepareLogoUpload(new Uint8Array(await file.arrayBuffer()));
    if (!prepared.ok) {
      setErrorKey(problemKeys[prepared.problem]);
      return;
    }
    await run(async () => {
      await apiRequest(path, { method: 'PUT', body: prepared.request, csrfToken });
    }, 'uploaded');
  }

  return (
    <Box component="section" aria-labelledby="logo-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" id="logo-title" gutterBottom>
        {translate(locale, 'logo.title')}
      </Typography>
      <Typography color="text.secondary" gutterBottom>
        {translate(locale, 'logo.note').replace(
          '{kilobytes}',
          String(Math.floor(maximumLogoBytes / 1024)),
        )}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {saved !== null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, saved === 'uploaded' ? 'logo.uploaded' : 'logo.removed')}
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'center' }}>
        {logoVersion === null ? (
          <Typography>{translate(locale, 'logo.none')}</Typography>
        ) : (
          <Box
            component="img"
            src={logoAddress(organizationId, logoVersion)}
            alt={translate(locale, 'logo.current')}
            sx={{ height: 64, maxWidth: 200, objectFit: 'contain' }}
          />
        )}
        <Button variant="contained" component="label" disabled={busy}>
          {translate(locale, logoVersion === null ? 'logo.choose' : 'logo.replace')}
          <input
            type="file"
            hidden
            accept={logoFileAccept}
            onChange={(event) => {
              const input = event.currentTarget;
              void choose(input.files?.[0]).finally(() => {
                input.value = '';
              });
            }}
          />
        </Button>
        {logoVersion !== null && (
          <Button
            variant="outlined"
            color="error"
            disabled={busy}
            onClick={() => {
              void run(async () => {
                await apiRequest(path, { method: 'DELETE', csrfToken });
              }, 'removed');
            }}
          >
            {translate(locale, 'logo.remove')}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
